import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";
import { DeployAPIService, type DeploysListResponse } from "@rails_api_base";
import { DeployTaskAPIService, type DeployTasksResponse, type DeployTaskRecord } from "@rails_api_base";
import { useJwt, useRootPath } from "~/stores/sessionStore";
import { Deploy } from "@shared";

export type { DeployRecord, DeploysListResponse } from "@rails_api_base";
export type { DeployTaskRecord, DeployTasksResponse } from "@rails_api_base";

const TERMINAL_STATUSES = Deploy.TERMINAL_DEPLOY_STATUSES as readonly string[];

// ============================================================================
// Query Keys
// ============================================================================

export const deploysKeys = {
  all: ["deploys"] as const,
  lists: () => [...deploysKeys.all, "list"] as const,
  list: (params: { project_id: number; page: number; campaign_id?: number }) =>
    [...deploysKeys.lists(), params] as const,
  tasks: (deployId: number) => [...deploysKeys.all, "tasks", deployId] as const,
};

// ============================================================================
// Service Hooks
// ============================================================================

export function useDeployService() {
  const jwt = useJwt();
  const rootPath = useRootPath();
  return useMemo(
    () => new DeployAPIService({ jwt: jwt ?? "", baseUrl: rootPath ?? "" }),
    [jwt, rootPath]
  );
}

export function useDeployTaskService() {
  const jwt = useJwt();
  const rootPath = useRootPath();
  return useMemo(
    () => new DeployTaskAPIService({ jwt: jwt ?? "", baseUrl: rootPath ?? "" }),
    [jwt, rootPath]
  );
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch paginated deploy history. Project-wide by default; pass campaignId
 * to scope to a specific campaign's deploy history (so /campaigns/:uuid/deploy
 * doesn't mix in deploys from sibling campaigns).
 */
export function useDeploys(projectId: number, page: number, campaignId?: number) {
  const service = useDeployService();
  const rootPath = useRootPath();

  return useQuery<DeploysListResponse>({
    queryKey: deploysKeys.list({ project_id: projectId, page, campaign_id: campaignId }),
    queryFn: () =>
      service.list({ project_id: projectId, page, campaign_id: campaignId }),
    enabled: projectId > 0 && !!rootPath,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Poll deploy tasks every 3 seconds. Stops when deploy reaches a terminal status.
 * Seeds initial data from Inertia props so first render has data immediately.
 */
export function useDeployTasks(
  deployId: number | undefined,
  initialTasks?: DeployTaskRecord[],
  initialDeployStatus?: string
) {
  const service = useDeployTaskService();
  const rootPath = useRootPath();

  return useQuery<DeployTasksResponse>({
    queryKey: deploysKeys.tasks(deployId ?? 0),
    queryFn: () => service.getTasks(deployId!),
    enabled: !!deployId && deployId > 0 && !!rootPath,
    initialData: deployId && initialTasks
      ? { deploy_status: initialDeployStatus ?? "pending", tasks: initialTasks }
      : undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.deploy_status;
      if (status && TERMINAL_STATUSES.includes(status)) return false;
      return 3000;
    },
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Roll back a completed deploy to a previous version.
 */
export function useRollbackDeploy() {
  const service = useDeployService();
  return useMutation({
    mutationFn: (deployId: number) => service.rollback(deployId),
  });
}

/**
 * Cancel an in-progress deploy.
 */
export function useCancelDeploy() {
  const service = useDeployService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deployId: number) => service.cancel(deployId),
    onSuccess: (_data, deployId) => {
      queryClient.invalidateQueries({ queryKey: deploysKeys.tasks(deployId) });
    },
  });
}

/**
 * Complete a user-blocking deploy task (e.g., "I accepted the invite", "I added payment").
 * Optimistically marks the task as completed in the React Query cache.
 * Fire-and-forget: no rollback on error.
 */
export function useCompleteDeployTask(deployId: number | undefined) {
  const service = useDeployTaskService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskName: string) =>
      service.updateTask(deployId!, taskName, { status: "completed" }),
    onMutate: async (taskName: string) => {
      if (!deployId) return;
      const queryKey = deploysKeys.tasks(deployId);
      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<DeployTasksResponse>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          tasks: old.tasks.map((t) =>
            t.name === taskName ? { ...t, status: "completed", completed_at: new Date().toISOString() } : t
          ),
        };
      });
    },
  });
}

